/**
 * Export utilities for chat with enterprise-friendly explainability.
 * Outputs categorised Information / Evidence / Rules with source attribution.
 */

import { generateExplainabilitySummary } from './explainabilitySummary';

function formatTimestamp(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
}

function buildCategoryMarkdown(title, items) {
  if (!items || items.length === 0) return '';
  const lines = [`### ${title}\n`];
  items.forEach(item => {
    let entry = '- ';
    if (item.label) entry += `**${item.label}**`;
    if (item.label && item.description) entry += ` — ${item.description}`;
    else if (item.description) entry += item.description;
    const parts = [];
    if (item.sourceDoc) parts.push(`Source: ${item.sourceDoc}`);
    if (item.createdBy) parts.push(`Added by ${item.createdBy}`);
    if (item.createDate) parts.push(item.createDate);
    if (parts.length > 0) entry += `  \n  *${parts.join(' | ')}*`;
    lines.push(entry);
  });
  lines.push('');
  return lines.join('\n');
}

function buildTrailMarkdown(steps) {
  if (!steps || steps.length === 0) return '';
  const lines = ['### How the system connected the dots\n'];
  steps.forEach((step, idx) => {
    let entry = `${idx + 1}. **${step.label || 'Unknown'}**`;
    if (step.description) entry += ` — ${step.description}`;
    if (step.sourceDoc) entry += `  \n   *Source: ${step.sourceDoc}*`;
    lines.push(entry);
    if (idx < steps.length - 1) {
      lines.push(`   ↓ *${step.relation || 'relates to'}*`);
    }
  });
  lines.push('');
  return lines.join('\n');
}

function buildNarrativeMarkdown(summary) {
  if (!summary) return '';
  const lines = [];

  if (summary.totalUnits > 0) {
    lines.push('Your question triggered these information units in our system of records.\n');
  }

  lines.push(buildCategoryMarkdown('Information', summary.information));
  lines.push(buildCategoryMarkdown('Evidence', summary.evidence));
  lines.push(buildCategoryMarkdown('Rules', summary.rules));
  lines.push(buildTrailMarkdown(summary.trailSteps));

  if (summary.confidenceSentence) {
    lines.push('### Confidence\n');
    lines.push(`${summary.confidenceSentence}\n`);
  }

  return lines.filter(l => l).join('\n');
}

function buildMessageMarkdown(message, index) {
  const lines = [];
  const num = index + 1;

  if (message.type === 'user') {
    lines.push(`## Message ${num} (User)`);
    lines.push(`*${formatTimestamp(message.timestamp)}*\n`);
    lines.push(`> ${message.content}\n`);
    return lines.join('\n');
  }

  if (message.type === 'error') {
    lines.push(`## Message ${num} (Error)`);
    lines.push(`*${formatTimestamp(message.timestamp)}*\n`);
    lines.push(`**Error:** ${message.content}\n`);
    return lines.join('\n');
  }

  if (message.type === 'process_assistant') {
    lines.push(`## Message ${num} (Process Execution)`);
    lines.push(`*${formatTimestamp(message.timestamp)}*\n`);
    lines.push(`${message.content}\n`);
    return lines.join('\n');
  }

  if (message.type !== 'assistant') return '';

  const isConversational = message.metadata?.routing === 'CONVERSATIONAL';

  lines.push(`## Message ${num} (Assistant)`);
  lines.push(`*${formatTimestamp(message.timestamp)}*\n`);
  lines.push(`${message.content}\n`);

  if (isConversational) {
    lines.push('*Conversational response — no system information lookup was needed.*\n');
    return lines.join('\n');
  }

  const summary = generateExplainabilitySummary(message.metadata);
  if (summary) {
    lines.push(buildNarrativeMarkdown(summary));
  }

  return lines.join('\n');
}

/**
 * Export a single message explanation as human-readable Markdown.
 */
export function exportSingleMessageMarkdown(message, summary) {
  const lines = [];
  lines.push('# Explanation Report\n');
  lines.push(`**Exported:** ${new Date().toLocaleString()}\n`);
  lines.push('---\n');
  lines.push(`**Answer:** ${message.content}\n`);

  if (summary) {
    lines.push(buildNarrativeMarkdown(summary));
  }

  return lines.join('\n');
}

/**
 * Export entire chat as Markdown with enterprise-friendly explainability.
 */
export function exportChatMarkdown(messages, graphId, settings, graphName) {
  const lines = [];
  lines.push(`# Chat Export — ${graphName || 'Unnamed Graph'}\n`);
  lines.push(`**Exported:** ${new Date().toLocaleString()}`);
  lines.push(`**Graph:** ${graphName || graphId}\n`);
  lines.push('---\n');

  const exportable = messages.filter(m => m.type !== 'loading' && m.type !== 'streaming');
  exportable.forEach((msg, idx) => {
    const section = buildMessageMarkdown(msg, idx);
    if (section) {
      lines.push(section);
      lines.push('---\n');
    }
  });

  return lines.join('\n');
}

/**
 * Export entire chat as JSON (raw data with metadata).
 */
export function exportChatJSON(messages, graphId, settings, graphName) {
  const exportData = {
    graphId,
    graphName: graphName || null,
    exportedAt: new Date().toISOString(),
    settings: {
      execution_mode: settings.execution_mode,
      snn_execution_mode: settings.snn_execution_mode,
      llm_model: settings.llm_model,
    },
    messages: messages.filter(m => m.type !== 'loading').map(m => ({
      type: m.type,
      content: m.content,
      timestamp: m.timestamp,
      metadata: m.metadata,
    })),
  };
  return JSON.stringify(exportData, null, 2);
}
